import * as THREE from "three";
import { BRICK_COLORS } from "./palette";

/**
 * Comfy-cute plush skin. Load once at boot; bricks/arena read from here.
 */
const loader = new THREE.TextureLoader();

export const plush = {
  ready: false,
  fiber: null as THREE.Texture | null,
  navy: null as THREE.Texture | null,
  maps: new Map<number, THREE.Texture>(),
};

function load(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (tex) => {
        tex.colorSpace = THREE.SRGBColorSpace;
        tex.wrapS = THREE.ClampToEdgeWrapping;
        tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.anisotropy = 4;
        resolve(tex);
      },
      undefined,
      reject,
    );
  });
}

export async function loadPlushTextures(): Promise<void> {
  if (plush.ready) return;
  try {
    const [fiber, cyan, red, amber, yellow, navy] = await Promise.all([
      load("/textures/plush-fiber.jpg"),
      load("/textures/plush-cyan.jpg"),
      load("/textures/plush-red.jpg"),
      load("/textures/plush-amber.jpg"),
      load("/textures/plush-yellow.jpg"),
      load("/textures/plush-navy.jpg"),
    ]);
    plush.fiber = fiber;
    plush.navy = navy;
    const C = BRICK_COLORS;
    plush.maps.set(C.cyan, cyan);
    plush.maps.set(C.brightRed, red);
    plush.maps.set(C.red, red);
    plush.maps.set(C.orange, amber);
    plush.maps.set(C.yellow, yellow);
    plush.maps.set(C.lime, yellow);
    plush.maps.set(C.darkBlue, navy);
    plush.maps.set(C.black, navy);
    plush.maps.set(C.blue, navy);
    plush.ready = true;
  } catch (err) {
    console.warn("plush textures failed — falling back to plastic", err);
  }
}

export function closestPlushMap(color: number): THREE.Texture | null {
  if (plush.maps.has(color)) return plush.maps.get(color)!;
  // nearest palette swatch by rgb
  let best: THREE.Texture | null = null;
  let bestD = Infinity;
  const c = new THREE.Color(color);
  for (const [hex, tex] of plush.maps) {
    const o = new THREE.Color(hex);
    const d = (c.r - o.r) ** 2 + (c.g - o.g) ** 2 + (c.b - o.b) ** 2;
    if (d < bestD) {
      bestD = d;
      best = tex;
    }
  }
  return best;
}

export function makePlushMaterial(color: number, emissive = false): THREE.MeshStandardMaterial {
  const map = closestPlushMap(color);
  const bump = plush.fiber;
  return new THREE.MeshStandardMaterial({
    color: map ? 0xffffff : color,
    map: map ?? undefined,
    bumpMap: bump ?? undefined,
    bumpScale: 0.14,
    roughness: 0.92,
    metalness: 0,
    emissive: emissive ? new THREE.Color(color) : new THREE.Color(0x000000),
    emissiveIntensity: emissive ? 0.18 : 0,
  });
}

export function makeFloorPlushMaterial(): THREE.MeshStandardMaterial {
  const map = plush.navy?.clone() ?? null;
  if (map) {
    map.wrapS = THREE.RepeatWrapping;
    map.wrapT = THREE.RepeatWrapping;
    map.repeat.set(8, 8);
  }
  return new THREE.MeshStandardMaterial({
    color: 0x1a1624,
    map: map ?? undefined,
    bumpMap: plush.fiber ?? undefined,
    bumpScale: 0.2,
    roughness: 0.95,
    metalness: 0,
  });
}
